import { useCallback, useEffect, useId, useMemo, useState } from "react";
import {
  Archive,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Code2,
  ImageIcon,
  Loader2,
  Pencil,
  RefreshCw,
  Sparkles,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { Button, cn } from "@nexus/ui";
import type { AddPreflightMarketInput } from "@nexus/contracts";
import { MarketMultiSelect } from "./MarketMultiSelect.js";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  useProvisionTenant,
  useAllIndustries,
  useTenantPicker,
} from "@/features/identity-hub/hooks/useIdentityHub";
import { identityHubKeys } from "@/lib/queries/identity-hub.queries";
import { usePermissions, useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  adminUpdateTenantSettings,
  fetchAdminTenantPreflightMarketRefs,
  fetchTenant,
  type TenantListItem,
} from "@/features/identity-hub/api/identity-hub.api";
import { selectAssignableIndustries } from "@/features/identity-hub/lib/assignable-industries";
import { BRAND_COLORS } from "@/features/identity-hub/lib/brand-colors";
import { TENANT_LOGO_ACCEPT } from "@/features/identity-hub/lib/tenant-logo-upload-constraints";
import { useTenantLogoUpload } from "@/features/identity-hub/hooks/useTenantLogoUpload";
import { isValidUuid, orgColor, orgInitials } from "@/features/identity-hub/utils/iam.utils";
import { orgNameError } from "@/features/identity-hub/lib/role-field-errors";
import {
  DEFAULT_TENANT_PRODUCT_LINE,
  isNewOrgDraftDirty,
  type TenantProductLine,
} from "@/features/identity-hub/lib/org-settings-dirty";
import { OrgDrawerUnsavedChangesGuard } from "./shared/drawer/OrgDrawerUnsavedChangesGuard";
import { IamOverlayDrawer } from "./shared/drawer/IamOverlayDrawer";
import { ORG_ACCENT_DRAWER_TOP_CLASS, orgAccentVar } from "@/features/identity-hub/utils/org-accent-surface";
import { OrgLogoMark } from "./OrgLogoMark";
import { AccentColorControl } from "./shared/AccentColorControl";
import {
  NewOrgAfterCopyingAside,
  NewOrgDrawerBodyLayout,
  NewOrgReadyToCreateAside,
  NewOrgSectionHeading,
  NewOrgWhenYouCreateAside,
} from "./new-org/NewOrgCreateAside";
import { NewOrgIndustryRows } from "./new-org/NewOrgIndustryRows";
import { NewOrgStepStrip, newOrgStepIndex, type NewOrgStep } from "./new-org/NewOrgStepStrip";
import { previewWorkspaceSlug } from "./new-org/preview-workspace-slug";
import {
  DEFAULT_COPY_SECTIONS,
  NewOrgCopyFromSection,
  type NewOrgCopySections,
  type NewOrgStartMode,
} from "./new-org/NewOrgCopyFromSection";
import {
  buildNewOrgStateFromCopySource,
  type NewOrgCopyFromFormState,
} from "./new-org/apply-copy-from-tenant";
import type { OrgPickerItem } from "./OrgPickerCombobox";

const INPUT_CLASS =
  "h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-ring focus:ring-1 focus:ring-ring/40";

const PRODUCT_LINE_OPTIONS = [
  { value: "CORE", labelKey: "iam.newOrgDialog.productLineCore", labelDefault: "Core", hintKey: "iam.newOrgDialog.productLineCoreHint", hintDefault: "Own brand" },
  { value: "COMP", labelKey: "iam.newOrgDialog.productLineComp", labelDefault: "Comp", hintKey: "iam.newOrgDialog.productLineCompHint", hintDefault: "Competitors" },
  { value: "CORE_AND_COMP", labelKey: "iam.newOrgDialog.productLineCoreAndComp", labelDefault: "Core & Comp", hintKey: "iam.newOrgDialog.productLineCoreAndCompHint", hintDefault: "Both · default" },
] as const;

function useTenantTypes(): Array<{ value: "ORGANIZATION" | "API"; label: string; description: string }> {
  const { t } = useTranslation();
  return [
    {
      value: "ORGANIZATION",
      label: t("iam.newOrgDialog.tenantType.organization"),
      description: t(
        "iam.newOrgDialog.tenantType.organizationDescription",
        "People sign in to dashboards, Opportunities and Pre Flight.",
      ),
    },
    {
      value: "API",
      label: t("iam.newOrgDialog.tenantType.api"),
      description: t(
        "iam.newOrgDialog.tenantType.apiDescription",
        "Machine access with API tokens. No seats or branding.",
      ),
    },
  ];
}

interface NewOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  superTenantId: string | null;
  onInviteFirstAdmin?: () => void;
}

export function NewOrgDialog({
  open,
  onOpenChange,
  superTenantId,
  onInviteFirstAdmin,
}: NewOrgDialogProps) {
  const { t } = useTranslation();
  const tenantTypes = useTenantTypes();

  const [phase, setPhase] = useState<"wizard" | "success">("wizard");
  const [step, setStep] = useState<NewOrgStep>("basics");
  const [maxVisitedStep, setMaxVisitedStep] = useState<NewOrgStep>("basics");

  const [name, setName] = useState("");
  const [isNameTouched, setIsNameTouched] = useState(false);
  const nameErrorId = useId();
  const [type, setType] = useState<"ORGANIZATION" | "API">("ORGANIZATION");
  const [productLine, setProductLine] = useState<TenantProductLine>(DEFAULT_TENANT_PRODUCT_LINE);
  const [industryIndices, setIndustryIndices] = useState<string[]>([]);
  const [preflightMarketRefs, setPreflightMarketRefs] = useState<AddPreflightMarketInput[]>([]);
  const [defaultIndustry, setDefaultIndustry] = useState<string | null>(null);
  const [defaultBrand, setDefaultBrand] = useState("");
  const [color, setColor] = useState<string | null>(BRAND_COLORS[0].value);
  const [logoFile, setLogoFile] = useState<{ name: string; sizeBytes: number } | null>(null);
  const [description, setDescription] = useState("");
  const [retoolUserId, setRetoolUserId] = useState("");
  const [retoolTouched, setRetoolTouched] = useState(false);
  const [showRetoolField, setShowRetoolField] = useState(false);

  const [createdTenantId, setCreatedTenantId] = useState<string | null>(null);
  const [pendingSettingsPayload, setPendingSettingsPayload] = useState<{
    defaultIndustry?: string | null;
    defaultBrand?: string | null;
  } | null>(null);
  const [settingsSaveFailed, setSettingsSaveFailed] = useState(false);
  const [settingsRetryPending, setSettingsRetryPending] = useState(false);
  const [unsavedCloseGuardOpen, setUnsavedCloseGuardOpen] = useState(false);
  const [copyFromTenantId, setCopyFromTenantId] = useState("");
  const [copyFromApplyPending, setCopyFromApplyPending] = useState(false);
  const [startMode, setStartMode] = useState<NewOrgStartMode>("copy");
  const [copySections, setCopySections] = useState<NewOrgCopySections>(DEFAULT_COPY_SECTIONS);
  const [copiedSnapshot, setCopiedSnapshot] = useState<NewOrgCopyFromFormState | null>(null);

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const logoUpload = useTenantLogoUpload({
    tenantId: activeTenantId,
    onUploaded: (confirmed) => setLogoUrl(confirmed.publicUrl ?? null),
  });
  const { previewUrl: logoPreviewUrl, progress: logoUploadProgress, inputRef: logoInputRef } = logoUpload;

  const qc = useQueryClient();
  const provisionTenant = useProvisionTenant();
  const tenantPickerQuery = useTenantPicker();
  const { data: industries = [] } = useAllIndustries();
  const perms = usePermissions();
  const isSuperTenant = useIsSuperTenant();
  const hasPermission = perms.IDENTITY_HUB_CREATE_TENANT.isOn && isSuperTenant;
  const isLogoUploading = logoUpload.isUploading;
  const hasLogo = Boolean(logoPreviewUrl || logoUrl);

  const nameTrimmed = name.trim();
  const workspaceSlug = previewWorkspaceSlug(nameTrimmed);
  const previewAccent = color || orgColor(nameTrimmed || "Org");
  const nameError = orgNameError(t, name);
  const showNameError = nameError !== null && (isNameTouched || name.length > 0);
  const descError = description.length > 500 ? t("iam.validation.descTooLong") : null;
  const retoolError =
    retoolUserId && !isValidUuid(retoolUserId) ? t("iam.validation.invalidUuid") : null;

  const canSubmit =
    !nameError &&
    !descError &&
    !retoolError &&
    !!superTenantId &&
    hasPermission &&
    !provisionTenant.isPending &&
    !isLogoUploading;

  const productLineLabel = useMemo(() => {
    const match = PRODUCT_LINE_OPTIONS.find((o) => o.value === productLine);
    return match ? t(match.labelKey, match.labelDefault) : productLine;
  }, [productLine, t]);

  const typeLabel =
    type === "ORGANIZATION"
      ? t("iam.newOrgDialog.tenantType.organization")
      : t("iam.newOrgDialog.tenantType.api");

  const assignableIndustryCount = useMemo(
    () => selectAssignableIndustries(industries).length,
    [industries],
  );

  const industriesSummary =
    industryIndices.length === 0
      ? t("iam.newOrgDialog.aside.reviewNone", "None")
      : t("iam.newOrgDialog.aside.reviewIndustriesCount", "{{count}} selected", {
          count: industryIndices.length,
        });

  const marketsSummary =
    preflightMarketRefs.length === 0
      ? t("iam.newOrgDialog.aside.reviewGlobalOnly", "Global only")
      : t("iam.newOrgDialog.aside.reviewGlobalPlus", "Global + {{count}}", {
          count: preflightMarketRefs.length,
        });

  const copySourcePickerItems = useMemo((): OrgPickerItem[] => {
    return (tenantPickerQuery.data?.items ?? [])
      .filter((t) => !t.deletedAt)
      .map((t) => ({
        id: t.id,
        name: t.name,
        type: t.type,
        logoUrl: t.logoUrl,
        brandColor: t.brandColor,
      }));
  }, [tenantPickerQuery.data?.items]);

  const copyFromOrgName = useMemo(() => {
    if (!copyFromTenantId) return undefined;
    return copySourcePickerItems.find((o) => o.id === copyFromTenantId)?.name;
  }, [copyFromTenantId, copySourcePickerItems]);

  const tenantSnapshotById = useMemo(() => {
    const map = new Map<string, TenantListItem>();
    for (const row of tenantPickerQuery.data?.items ?? []) {
      if (!row.deletedAt) map.set(row.id, row);
    }
    return map;
  }, [tenantPickerQuery.data?.items]);

  const paintCopiedSnapshot = useCallback(
    (next: NewOrgCopyFromFormState, sections: NewOrgCopySections) => {
      if (sections.productLineIndustries) {
        setProductLine(next.productLine);
        setIndustryIndices(next.industryIndices);
        setDefaultIndustry(next.defaultIndustry);
      }
      if (sections.defaultBrand) setDefaultBrand(next.defaultBrand);
      if (sections.accent) setColor(next.color);
      setPreflightMarketRefs(sections.preflight ? next.preflightMarketRefs : []);
      logoUpload.clearPreview();
      setLogoUrl(null);
      setLogoFile(null);
    },
    [logoUpload],
  );

  const applyCopyFromTenant = useCallback(
    async (tenantId: string, sections: NewOrgCopySections) => {
      if (!tenantId) return;
      setCopyFromApplyPending(true);
      try {
        const fromPicker = tenantSnapshotById.get(tenantId);
        const source = fromPicker ?? (await fetchTenant(tenantId));
        const marketRefs = await fetchAdminTenantPreflightMarketRefs(tenantId);
        const next = buildNewOrgStateFromCopySource(source, marketRefs);
        setCopiedSnapshot(next);
        setType(next.type);
        paintCopiedSnapshot(next, sections);
      } finally {
        setCopyFromApplyPending(false);
      }
    },
    [tenantSnapshotById, paintCopiedSnapshot],
  );

  const clearCopiedFields = () => {
    setProductLine(DEFAULT_TENANT_PRODUCT_LINE);
    setIndustryIndices([]);
    setPreflightMarketRefs([]);
    setDefaultIndustry(null);
    setDefaultBrand("");
    setColor(BRAND_COLORS[0].value);
    setCopiedSnapshot(null);
  };

  const handleCopyFromTenantChange = (tenantId: string) => {
    setCopyFromTenantId(tenantId);
    void applyCopyFromTenant(tenantId, copySections);
  };

  const handleStartModeChange = (mode: NewOrgStartMode) => {
    setStartMode(mode);
    if (mode === "blank") {
      setCopyFromTenantId(superTenantId ?? "");
      clearCopiedFields();
    }
  };

  const handleSelectAllCopySections = () => {
    const nextSections: NewOrgCopySections = {
      productLineIndustries: true,
      defaultBrand: true,
      roles: true,
      accent: true,
      preflight: true,
    };
    setCopySections(nextSections);
    if (copiedSnapshot) paintCopiedSnapshot(copiedSnapshot, nextSections);
  };

  const handleCopySectionChange = (key: keyof NewOrgCopySections, checked: boolean) => {
    const nextSections = { ...copySections, [key]: checked };
    setCopySections(nextSections);
    if (!copiedSnapshot) return;
    if (!checked) {
      if (key === "productLineIndustries") {
        setProductLine(DEFAULT_TENANT_PRODUCT_LINE);
        setIndustryIndices([]);
        setDefaultIndustry(null);
      }
      if (key === "defaultBrand") setDefaultBrand("");
      if (key === "accent") setColor(BRAND_COLORS[0].value);
      if (key === "preflight") setPreflightMarketRefs([]);
      return;
    }
    paintCopiedSnapshot(copiedSnapshot, nextSections);
  };

  useEffect(() => {
    if (!open || !superTenantId) return;
    setCopyFromTenantId(superTenantId);
  }, [open, superTenantId]);

  const reset = () => {
    setPhase("wizard");
    setStep("basics");
    setMaxVisitedStep("basics");
    setName("");
    setIsNameTouched(false);
    setType("ORGANIZATION");
    setProductLine(DEFAULT_TENANT_PRODUCT_LINE);
    setIndustryIndices([]);
    setPreflightMarketRefs([]);
    setDefaultIndustry(null);
    setDefaultBrand("");
    setColor(BRAND_COLORS[0].value);
    setDescription("");
    setRetoolUserId("");
    setRetoolTouched(false);
    setShowRetoolField(false);
    setLogoUrl(null);
    setLogoFile(null);
    logoUpload.clearPreview();
    setCreatedTenantId(null);
    setPendingSettingsPayload(null);
    setSettingsSaveFailed(false);
    setSettingsRetryPending(false);
    setCopyFromTenantId(superTenantId ?? "");
    setCopyFromApplyPending(false);
    setStartMode("copy");
    setCopySections(DEFAULT_COPY_SECTIONS);
    setCopiedSnapshot(null);
  };

  const isDraftDirty = isNewOrgDraftDirty({
    phase,
    name,
    type,
    productLine,
    industryIndices,
    preflightMarketCount: preflightMarketRefs.length,
    defaultIndustry,
    defaultBrand,
    description,
    retoolUserId,
    hasLogo,
    brandColor: color,
    defaultBrandColor: BRAND_COLORS[0].value,
    visitedBeyondBasics: maxVisitedStep !== "basics",
  });

  const closeDrawer = () => {
    setUnsavedCloseGuardOpen(false);
    reset();
    onOpenChange(false);
  };

  const handleOpenChange = (v: boolean) => {
    if (v) {
      onOpenChange(true);
      return;
    }
    if (isDraftDirty) {
      setUnsavedCloseGuardOpen(true);
      return;
    }
    closeDrawer();
  };

  const visitStep = (next: NewOrgStep) => {
    setStep(next);
    if (newOrgStepIndex(next) > newOrgStepIndex(maxVisitedStep)) {
      setMaxVisitedStep(next);
    }
  };

  const handleLogoRemove = () => {
    logoUpload.clearPreview();
    setLogoUrl(null);
    setLogoFile(null);
  };

  const buildSettingsPayload = () => {
    const effectiveDefault = industryIndices.includes(defaultIndustry ?? "")
      ? defaultIndustry
      : industryIndices.length === 1
        ? industryIndices[0]!
        : null;
    const trimmedBrand = defaultBrand.trim();
    const settingsPayload: { defaultIndustry?: string | null; defaultBrand?: string | null } = {};
    if (effectiveDefault) settingsPayload.defaultIndustry = effectiveDefault;
    if (trimmedBrand) settingsPayload.defaultBrand = trimmedBrand;
    return settingsPayload;
  };

  const applyTenantSettings = async (tenantId: string, settingsPayload: ReturnType<typeof buildSettingsPayload>) => {
    if (Object.keys(settingsPayload).length === 0) return true;
    await adminUpdateTenantSettings(tenantId, settingsPayload);
    void qc.invalidateQueries({ queryKey: identityHubKeys.all });
    return true;
  };

  const handleSubmit = async () => {
    if (!superTenantId || !hasPermission) return;
    setRetoolTouched(true);
    if (retoolError) return;

    try {
      const result = await provisionTenant.mutateAsync({
        name: nameTrimmed,
        type,
        tenantId: superTenantId,
        copyAccessFromTenantId:
          startMode === "copy" && copyFromTenantId && copySections.roles ? copyFromTenantId : superTenantId,
        productLine,
        retoolUserId: retoolUserId.trim() || null,
        ...(description.trim() ? { description: description.trim() } : {}),
        brandColor: color,
        ...(logoUrl ? { logoUrl } : {}),
        ...(industryIndices.length > 0 ? { industryIndices } : {}),
        ...(preflightMarketRefs.length > 0 ? { preflightMarketRefs } : {}),
      });

      const tenantId = result.tenantId as string | undefined;
      const settingsPayload = buildSettingsPayload();
      setCreatedTenantId(tenantId ?? null);
      setPendingSettingsPayload(Object.keys(settingsPayload).length > 0 ? settingsPayload : null);
      setSettingsSaveFailed(false);

      if (tenantId && Object.keys(settingsPayload).length > 0) {
        try {
          await applyTenantSettings(tenantId, settingsPayload);
        } catch {
          setSettingsSaveFailed(true);
        }
      }

      setPhase("success");
    } catch {
      // onError toast shown by useProvisionTenant
    }
  };

  const handleRetrySettings = async () => {
    if (!createdTenantId || !pendingSettingsPayload) return;
    setSettingsRetryPending(true);
    try {
      await applyTenantSettings(createdTenantId, pendingSettingsPayload);
      setSettingsSaveFailed(false);
    } catch {
      setSettingsSaveFailed(true);
    } finally {
      setSettingsRetryPending(false);
    }
  };

  const isPending = provisionTenant.isPending;
  const stepIndex = newOrgStepIndex(step);

  const footerContinueLabel =
    step === "basics" && startMode === "copy" && copyFromTenantId && copyFromTenantId !== superTenantId
      ? t("iam.newOrgDialog.copyFrom.reviewCopied", "Review copied settings")
      : step === "basics"
        ? t("iam.newOrgDialog.continueDataAccess", "Continue to data access")
        : t("iam.newOrgDialog.continueBranding", "Continue to branding");

  const aside =
    step === "branding" ? (
      <NewOrgReadyToCreateAside
        typeLabel={typeLabel}
        productLineLabel={productLineLabel}
        industriesSummary={industriesSummary}
        marketsSummary={marketsSummary}
        defaultBrand={defaultBrand.trim() || t("iam.shared.none", "None")}
        accentLabel={color ?? t("iam.accentColor.sourceAutoLong", "Auto from name")}
      />
    ) : startMode === "copy" && copyFromOrgName && copyFromTenantId !== superTenantId ? (
      <NewOrgAfterCopyingAside
        sourceName={copyFromOrgName}
        newName={nameTrimmed}
      />
    ) : (
      <NewOrgWhenYouCreateAside />
    );

  return (
    <IamOverlayDrawer
      open={open}
      onOpenChange={handleOpenChange}
      title={t("iam.newOrgDialog.title")}
      description={t("iam.newOrgDialog.description")}
      className={ORG_ACCENT_DRAWER_TOP_CLASS}
      style={orgAccentVar(previewAccent)}
    >
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Live preview header */}
        <div className="flex shrink-0 items-start gap-3 border-b border-border-subtle bg-[linear-gradient(145deg,color-mix(in_srgb,var(--org-accent)_14%,var(--card))_0%,color-mix(in_srgb,var(--org-accent)_5%,var(--card))_50%,var(--card)_100%)] px-5 py-4">
          {logoPreviewUrl || logoUrl ? (
            <img
              src={logoPreviewUrl ?? logoUrl ?? undefined}
              alt={t("iam.newOrgDialog.logoPreviewAlt")}
              className="size-11 shrink-0 overflow-hidden rounded-lg border border-border/60 bg-background object-cover"
            />
          ) : phase === "success" && createdTenantId ? (
            <OrgLogoMark
              name={nameTrimmed}
              logoUrl={logoUrl}
              accent={previewAccent}
              size={44}
            />
          ) : (
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border text-[15px] font-semibold text-white"
              style={{
                backgroundColor: previewAccent,
                borderColor: `color-mix(in srgb, ${previewAccent} 70%, white)`,
              }}
            >
              {nameTrimmed ? orgInitials(nameTrimmed) : "…"}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                {t("iam.newOrgDialog.title")}
              </span>
              {phase === "wizard" && (
                <span className="inline-flex items-center rounded-full border border-warning-border bg-warning-subtle px-1.5 py-[3px] text-[10px] font-medium leading-none text-warning-subtle-foreground">
                  {t("iam.newOrgDialog.draftBadge", "Draft · not saved")}
                </span>
              )}
              {phase === "success" && (
                <span className="rounded-full border border-success-subtle bg-success-subtle px-1.5 py-[3px] text-[10px] font-medium text-success-subtle-foreground">
                  {t("iam.newOrgDialog.createdBadge", "Created")}
                </span>
              )}
            </div>
            <h3 className="truncate text-[15px] font-semibold leading-tight">
              {nameTrimmed || t("iam.newOrgDialog.untitledOrg", "Untitled organization")}
            </h3>
            <p className="text-[11px] text-muted-foreground">
              {typeLabel} · {productLineLabel}
              {workspaceSlug !== "…" ? ` · ${workspaceSlug}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={t("iam.orgDrawer.closePanel", "Close panel")}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {phase === "wizard" && (
          <>
            <NewOrgStepStrip
              activeStep={step}
              maxVisitedStep={maxVisitedStep}
              onStepChange={visitStep}
            />

            <NewOrgDrawerBodyLayout aside={aside}>
              {step === "basics" && (
                <div className="flex flex-col gap-7">
                  <NewOrgCopyFromSection
                    mode={startMode}
                    onModeChange={handleStartModeChange}
                    sections={copySections}
                    onSectionChange={handleCopySectionChange}
                    onSelectAll={handleSelectAllCopySections}
                    items={copySourcePickerItems}
                    value={copyFromTenantId}
                    onValueChange={handleCopyFromTenantChange}
                    sourceName={copyFromOrgName}
                    sourceMeta={(() => {
                      const source = tenantSnapshotById.get(copyFromTenantId);
                      if (!source || source.id === superTenantId) return undefined;
                      const members =
                        source.memberCount != null
                          ? t("iam.newOrgDialog.copyFrom.memberCount", "{{count}} members", {
                              count: source.memberCount,
                            })
                          : null;
                      return [source.type === "API" ? "API" : "Organization", members]
                        .filter(Boolean)
                        .join(" · ");
                    })()}
                    sectionDetails={{
                      productLineIndustries: productLineLabel,
                      defaultBrand:
                        defaultBrand.trim() ||
                        t("iam.newOrgDialog.copyFrom.detailNone", "None set"),
                      roles: t(
                        "iam.newOrgDialog.copyFrom.detailRoles",
                        "Tenant roles copied with access",
                      ),
                      accent: color ?? t("iam.accentColor.sourceAutoLong", "Auto from name"),
                      preflight: copySections.preflight
                        ? marketsSummary
                        : t(
                            "iam.newOrgDialog.copyFrom.detailPreflightOff",
                            "Off by default — markets stay specific to the source org",
                          ),
                    }}
                    loading={copyFromApplyPending || tenantPickerQuery.isLoading}
                    disabled={!hasPermission}
                  />

                  <section className="flex flex-col gap-4">
                    <NewOrgSectionHeading
                      title={t("iam.newOrgDialog.identitySectionTitle", "Who is it for")}
                      hint={t(
                        "iam.newOrgDialog.identitySectionHint",
                        "The header above updates as you type — that’s how it will look in Identity Hub.",
                      )}
                    />
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_220px]">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-baseline justify-between gap-2">
                          <label className="text-xs font-medium text-foreground">
                            {t("iam.newOrgDialog.displayNameLabel")}{" "}
                            <span className="text-destructive">*</span>
                          </label>
                          <span className="text-[11px] tabular-nums text-muted-foreground">
                            {nameTrimmed.length} / 255
                          </span>
                        </div>
                        <input
                          type="text"
                          placeholder={t("iam.newOrgDialog.displayNamePlaceholder")}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          onBlur={() => setIsNameTouched(true)}
                          aria-label={t("iam.newOrgDialog.displayNameLabel")}
                          aria-invalid={showNameError || undefined}
                          aria-describedby={showNameError ? nameErrorId : undefined}
                          required
                          className={cn(INPUT_CLASS, showNameError && "border-destructive focus:border-destructive")}
                          autoFocus
                        />
                        {showNameError && <p id={nameErrorId} className="text-[11px] text-destructive">{nameError}</p>}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-foreground">
                          {t("iam.newOrgDialog.workspaceIdLabel", "Workspace ID")}
                        </label>
                        <div className="flex h-9 items-center justify-between rounded-md border border-input-border bg-muted px-3">
                          <span className="truncate font-mono text-xs text-foreground">{workspaceSlug}</span>
                          <Pencil className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          {t("iam.newOrgDialog.workspaceIdHint", "Generated from the name")}
                        </p>
                      </div>
                    </div>
                  </section>

                  <section className="flex flex-col gap-2">
                    <label className="text-xs font-medium text-foreground">
                      {t("iam.newOrgDialog.typeLabel")} <span className="text-destructive">*</span>
                    </label>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {tenantTypes.map((tt) => {
                        const selected = type === tt.value;
                        return (
                          <button
                            key={tt.value}
                            type="button"
                            onClick={() => setType(tt.value)}
                            className={cn(
                              "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors",
                              selected
                                ? "border-primary bg-primary-subtle"
                                : "border-border bg-background hover:bg-muted/40",
                            )}
                          >
                            <span
                              className={cn(
                                "mt-0.5 h-4 w-4 shrink-0 rounded-full border-[5px]",
                                selected ? "border-primary bg-background" : "border-input-border bg-background",
                              )}
                              aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-1.5 text-[13px] font-medium text-foreground">
                                {tt.value === "ORGANIZATION" ? (
                                  <Building2 className="h-3.5 w-3.5 text-primary" />
                                ) : (
                                  <Code2 className="h-3.5 w-3.5 text-muted-foreground" />
                                )}
                                {tt.label}
                              </span>
                              <span className="mt-0.5 block text-xs text-muted-foreground">{tt.description}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className="flex flex-col gap-2">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium text-foreground">
                        {t("iam.newOrgDialog.productLineLabel", "Product line")}
                      </span>
                      <span className="text-[11px] leading-[1.45] text-muted-foreground">
                        {t(
                          "iam.newOrgDialog.productLineHint",
                          "Controls which KPIs and menus the tenant sees.",
                        )}
                      </span>
                    </div>
                    <div
                      role="radiogroup"
                      aria-label={t("iam.newOrgDialog.productLineLabel", "Product line")}
                      className="grid grid-cols-3 gap-1 rounded-md border border-border bg-muted p-1"
                      data-testid="new-org-product-line-group"
                    >
                      {PRODUCT_LINE_OPTIONS.map((option) => {
                        const selected = productLine === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            data-testid={`new-org-product-line-${option.value}`}
                            onClick={() => setProductLine(option.value as TenantProductLine)}
                            className={cn(
                              "flex flex-col items-center gap-0.5 rounded-sm p-2 text-center transition-colors",
                              selected
                                ? "border border-border bg-background shadow-sm"
                                : "border border-transparent bg-transparent hover:bg-background/50",
                            )}
                          >
                            <span
                              className={cn(
                                "text-xs font-medium",
                                selected ? "text-primary" : "text-foreground",
                              )}
                            >
                              {t(option.labelKey, option.labelDefault)}
                            </span>
                            <span className="text-[11px] leading-[1.45] text-muted-foreground">
                              {t(option.hintKey, option.hintDefault)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className="flex flex-col gap-1.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <label className="text-xs font-medium text-foreground">
                        {t("iam.newOrgDialog.descriptionLabel")}
                        <span className="ml-1 font-normal text-muted-foreground">
                          {t("iam.newOrgDialog.internalNoteOptional", "(optional, admins only)")}
                        </span>
                      </label>
                      <span className="text-[11px] tabular-nums text-muted-foreground">
                        {description.length} / 500
                      </span>
                    </div>
                    <textarea
                      placeholder={t(
                        "iam.newOrgDialog.internalNotePlaceholder",
                        "e.g. Signed Q4 pilot — contact Lena Hoffmann",
                      )}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      maxLength={500}
                      className={cn(
                        "min-h-20 resize-none rounded-md border bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground/60 focus:border-ring focus:ring-1 focus:ring-ring/40",
                        descError ? "border-destructive" : "border-border",
                      )}
                    />
                    {descError && <p className="text-[11px] text-destructive">{descError}</p>}
                  </section>
                </div>
              )}

              {step === "dataAccess" && (
                <div className="flex flex-col gap-7">
                  <section className="flex flex-col gap-3">
                    <NewOrgSectionHeading
                      title={t("iam.newOrgDialog.industriesLabel")}
                      hint={t(
                        "iam.newOrgDialog.industriesSectionHint",
                        "Which industry data they can explore. The starred one is pre-selected in Create Opportunity Set.",
                      )}
                      trailing={
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {t("iam.newOrgDialog.industriesCount", "{{count}} of {{total}}", {
                            count: industryIndices.length,
                            total: assignableIndustryCount,
                          })}
                        </span>
                      }
                    />
                    <NewOrgIndustryRows
                      industries={industries}
                      selected={industryIndices}
                      defaultIndex={
                        defaultIndustry && industryIndices.includes(defaultIndustry)
                          ? defaultIndustry
                          : (industryIndices[0] ?? null)
                      }
                      onSelectedChange={(next) => {
                        setIndustryIndices(next);
                        setDefaultIndustry((current) =>
                          current && next.includes(current) ? current : (next[0] ?? null),
                        );
                      }}
                      onDefaultChange={setDefaultIndustry}
                    />
                  </section>

                  <section className="flex flex-col gap-3">
                    <NewOrgSectionHeading
                      title={t("iam.newOrgDialog.marketsLabel")}
                      hint={t(
                        "iam.newOrgDialog.marketsSectionHint",
                        "Each Pre Flight report is filed under one market. Global is always available.",
                      )}
                      trailing={
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {marketsSummary}
                        </span>
                      }
                    />
                    <MarketMultiSelect
                      layout="chips"
                      selected={preflightMarketRefs}
                      onChange={setPreflightMarketRefs}
                      placeholder={t("iam.newOrgDialog.marketsSelectPlaceholder", "Select markets…")}
                      ariaLabel={t("iam.newOrgDialog.marketsLabel")}
                    />
                  </section>

                  <section className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-foreground">
                      {t("iam.newOrgDialog.defaultBrandLabel", "Default brand")}
                      <span className="ml-1 font-normal text-muted-foreground">
                        {t("iam.newOrgDialog.defaultBrandOptional", "(optional)")}
                      </span>
                    </label>
                    <input
                      type="text"
                      placeholder={t("iam.newOrgDialog.defaultBrandPlaceholder", "e.g. Vodafone")}
                      value={defaultBrand}
                      onChange={(e) => setDefaultBrand(e.target.value)}
                      className={cn(INPUT_CLASS, "max-w-[360px] text-[13px]")}
                    />
                    {nameTrimmed ? (
                      <p className="flex items-center gap-1.5 text-[11px] leading-snug text-success-subtle-foreground">
                        <Sparkles className="size-3 shrink-0" />
                        {t(
                          "iam.newOrgDialog.defaultBrandSuggestion",
                          "Suggested from the display name · analysed in Pre Flight creative reports",
                        )}
                      </p>
                    ) : null}
                  </section>
                </div>
              )}

              {step === "branding" && (
                <div className="flex flex-col gap-7">
                  <section className="flex flex-col gap-2">
                    <NewOrgSectionHeading
                      title={t("iam.newOrgDialog.logoLabel")}
                      hint={t(
                        "iam.newOrgDialog.logoSectionHint",
                        "Shown in the header, org cards and the tenant switcher.",
                      )}
                    />
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept={TENANT_LOGO_ACCEPT}
                      className="sr-only"
                      disabled={isLogoUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setLogoFile({ name: file.name, sizeBytes: file.size });
                        void logoUpload.handleFileChange(file);
                      }}
                    />
                    <div className="flex items-center gap-3 rounded-lg border border-border bg-background p-3">
                      <div className="relative shrink-0">
                        {logoPreviewUrl ? (
                          <img
                            src={logoPreviewUrl}
                            alt={t("iam.newOrgDialog.logoPreviewAlt")}
                            className="size-12 overflow-hidden rounded-md border border-border/60 bg-background object-cover"
                          />
                        ) : (
                          <div className="flex size-12 items-center justify-center rounded-md bg-muted text-muted-foreground">
                            <ImageIcon className="size-5 opacity-40" />
                          </div>
                        )}
                        {isLogoUploading && (
                          <div className="absolute inset-0 flex items-center justify-center rounded-md bg-background/80">
                            <Loader2 className="size-4 animate-spin text-primary" />
                          </div>
                        )}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        {hasLogo ? (
                          <>
                            <p className="truncate text-xs font-medium text-foreground">
                              {logoFile?.name ?? t("iam.newOrgDialog.logoFileFallback", "Logo")}
                            </p>
                            {isLogoUploading ? (
                              <div className="mt-1 h-1 w-full max-w-[200px] overflow-hidden rounded-full bg-muted">
                                <div
                                  className="h-full rounded-full bg-primary transition-all duration-200"
                                  style={{ width: `${logoUploadProgress ?? 0}%` }}
                                />
                              </div>
                            ) : (
                              <p className="flex items-center gap-1 text-[11px] leading-snug text-success-subtle-foreground">
                                <Check className="size-3 shrink-0" />
                                {logoFile
                                  ? t("iam.newOrgDialog.logoUploadedWithSize", "Uploaded · {{size}} KB", {
                                      size: Math.max(1, Math.round(logoFile.sizeBytes / 1024)),
                                    })
                                  : t("iam.newOrgDialog.logoUploaded", "Uploaded")}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-[11px] text-muted-foreground">
                            {t("iam.orgDrawer.settings.logoHint")}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 px-2.5 text-[11px]"
                        iconStart={hasLogo ? "refresh-cw" : "upload"}
                        disabled={isLogoUploading}
                        onClick={() => logoInputRef.current?.click()}
                      >
                        {isLogoUploading
                          ? t("iam.orgDrawer.settings.logoUploading")
                          : hasLogo
                            ? t("iam.newOrgDialog.logoReplace", "Replace")
                            : t("iam.orgDrawer.settings.logoUpload")}
                      </Button>
                      {hasLogo && !isLogoUploading ? (
                        <button
                          type="button"
                          aria-label={t("iam.orgDrawer.settings.logoRemove")}
                          onClick={handleLogoRemove}
                          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      ) : null}
                    </div>
                  </section>

                  <AccentColorControl
                    layout="stacked"
                    showSectionHeading
                    orgName={nameTrimmed || t("iam.newOrgDialog.untitledOrg", "Untitled organization")}
                    brandColor={color}
                    onBrandColorChange={setColor}
                    logoUrl={logoPreviewUrl ?? logoUrl}
                    logoFileName={logoFile?.name}
                  />

                  <button
                    type="button"
                    aria-expanded={showRetoolField}
                    onClick={() => setShowRetoolField((v) => !v)}
                    className="flex w-full items-center gap-2 border-t border-border pt-4 text-left"
                  >
                    <Archive className="size-3.5 shrink-0 text-[#9ca3af]" />
                    <span className="text-[11px] leading-snug text-muted-foreground">
                      {t("iam.newOrgDialog.retoolLegacyLabel", "Legacy · Retool user ID")}
                    </span>
                    <span className="rounded-sm bg-border-subtle px-1.5 py-[3px] font-mono text-[9px] uppercase leading-none text-muted-foreground">
                      {t("iam.newOrgDialog.retoolDeprecated", "Deprecated")}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[11px] leading-snug text-[#9ca3af]">
                      {retoolUserId.trim() || t("iam.newOrgDialog.retoolNotSet", "Not set")}
                    </span>
                    <span className="text-[11px] leading-none text-muted-foreground">
                      {showRetoolField
                        ? t("iam.newOrgDialog.retoolHide", "Hide")
                        : t("iam.newOrgDialog.retoolShow", "Show")}
                    </span>
                    {showRetoolField ? (
                      <ChevronDown className="size-3 shrink-0 text-[#9ca3af]" />
                    ) : (
                      <ChevronRight className="size-3 shrink-0 text-[#9ca3af]" />
                    )}
                  </button>
                  {showRetoolField && (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-foreground">
                        {t("iam.newOrgDialog.retoolUserIdLabel")}
                      </label>
                      <input
                        type="text"
                        placeholder={t("iam.newOrgDialog.retoolUserIdPlaceholder")}
                        value={retoolUserId}
                        onChange={(e) => setRetoolUserId(e.target.value.trim())}
                        onBlur={() => setRetoolTouched(true)}
                        className={cn(
                          "h-9 rounded-md border bg-background px-3 font-mono text-xs outline-none focus:ring-1 focus:ring-ring/40",
                          retoolTouched && retoolError
                            ? "border-destructive"
                            : "border-border focus:border-ring",
                        )}
                      />
                      {retoolTouched && retoolError ? (
                        <p className="text-[11px] text-destructive">{retoolError}</p>
                      ) : (
                        <p className="text-[11px] text-muted-foreground">
                          {t("iam.newOrgDialog.retoolUserIdOptionalHint")}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </NewOrgDrawerBodyLayout>

            <div className="flex shrink-0 items-center gap-4 border-t border-border px-5 py-3">
              <div className="flex flex-1 items-center gap-3">
                <div className="flex w-24 shrink-0 gap-1">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className={cn(
                        "h-1 flex-1 rounded-full",
                        i <= stepIndex ? "bg-primary" : "bg-border",
                      )}
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">
                  {t("iam.newOrgDialog.stepCount", "Step {{current}} of {{total}}", {
                    current: stepIndex + 1,
                    total: 3,
                  })}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {step === "basics" ? (
                  <Button variant="ghost" size="sm" onClick={() => handleOpenChange(false)}>
                    {t("common.cancel")}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconStart="arrow-left"
                    onClick={() => visitStep(step === "branding" ? "dataAccess" : "basics")}
                  >
                    {t("iam.newOrgDialog.back", "Back")}
                  </Button>
                )}
                {step !== "branding" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={step === "basics" || !canSubmit}
                    onClick={() => void handleSubmit()}
                  >
                    {isPending ? t("iam.newOrgDialog.creating") : t("iam.newOrgDialog.createNow", "Create now")}
                  </Button>
                ) : null}
                {step !== "branding" ? (
                  <Button
                    size="sm"
                    className="gap-1.5"
                    iconEnd="arrow-right"
                    disabled={!canSubmit}
                    onClick={() =>
                      visitStep(step === "basics" ? "dataAccess" : "branding")
                    }
                  >
                    {footerContinueLabel}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="gap-1.5"
                    iconStart="check"
                    disabled={!canSubmit}
                    onClick={() => void handleSubmit()}
                  >
                    {isPending ? t("iam.newOrgDialog.creating") : t("iam.newOrgDialog.submit")}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {phase === "success" && (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-8 py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-subtle">
              <Check className="h-6 w-6 text-success-subtle-foreground" />
            </div>
            <div className="max-w-md space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t("iam.newOrgDialog.successTitle", "{{name}} is ready", { name: nameTrimmed })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t(
                  "iam.newOrgDialog.successDescription",
                  "The organization was created. Invite its first admin to get them signed in.",
                )}
              </p>
            </div>
            {settingsSaveFailed && pendingSettingsPayload && (
              <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-warning-border bg-warning-subtle px-4 py-3 text-left">
                <p className="text-sm text-warning-subtle-foreground">
                  {t(
                    "iam.newOrgDialog.settingsPartialFailure",
                    "The organization was created, but default industry/brand settings could not be saved.",
                  )}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={settingsRetryPending}
                  onClick={() => void handleRetrySettings()}
                >
                  {settingsRetryPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  {t("iam.newOrgDialog.settingsRetry", "Retry saving settings")}
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
                {t("iam.newOrgDialog.done", "Done")}
              </Button>
              {onInviteFirstAdmin && (
                <Button
                  size="sm"
                  className="gap-1.5"
                  onClick={() => {
                    handleOpenChange(false);
                    onInviteFirstAdmin();
                  }}
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  {t("iam.newOrgDialog.inviteFirstAdmin", "Invite first admin")}
                </Button>
              )}
            </div>
          </div>
        )}
        <OrgDrawerUnsavedChangesGuard
          open={unsavedCloseGuardOpen}
          translationScope="iam.newOrgDialog.unsavedGuard"
          onKeepEditing={() => setUnsavedCloseGuardOpen(false)}
          onDiscard={closeDrawer}
        />
      </div>
    </IamOverlayDrawer>
  );
}
